// Firebase Configuration and Utilities
import { initializeApp } from 'firebase/app';
import { 
  getFirestore, 
  collection, 
  doc,
  updateDoc,
  deleteDoc,
  arrayUnion,
  arrayRemove,
  setDoc, 
  getDoc, 
  query,
  where,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
  serverTimestamp,
  GeoPoint,
  addDoc,
  increment
} from 'firebase/firestore';
import { getMessaging, getToken, deleteToken } from 'firebase/messaging';
import { 
  getAuth, 
  signInWithPopup, 
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  sendEmailVerification as firebaseSendEmailVerification,
  reload as firebaseReload
} from 'firebase/auth';
import { 
  geohashForLocation,
  geohashQueryBounds,
  distanceBetween
} from 'geofire-common';

const firebaseConfig = {
  apiKey: "AIzaSyC3kQTbaNNYojEIx1jaU9p0C8K4ue7NFIs",
  authDomain: "gigwave-397a4.firebaseapp.com",
  projectId: "gigwave-397a4",
  storageBucket: "gigwave-397a4.firebasestorage.app",
  messagingSenderId: "253503195277",
  appId: "1:253503195277:web:c6c109ccee6fdbd9c57ac4"
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);

// ============================================
// GEOLOCATION UTILITIES
// ============================================

export const calculateDistance = (point1, point2) => {
  return distanceBetween(
    [point1.lat, point1.lng],
    [point2.lat, point2.lng]
  ) * 1000;
};

export const isWithinRange = (userLocation, venueLocation, radiusMeters = 100) => {
  const distance = calculateDistance(userLocation, venueLocation);
  return distance <= radiusMeters;
};

export const getUserLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({ lat: position.coords.latitude, lng: position.coords.longitude });
      },
      (error) => reject(error),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  });
};

export const searchNearbyGigs = async (userLocation, radiusKm = 50, statusFilter = null) => {
  try {
    console.log('🔍 SEARCH DEBUG: Starting search...');
    const center = [userLocation.lat, userLocation.lng];
    const bounds = geohashQueryBounds(center, radiusKm * 1000);
    const promises = [];
    for (const bound of bounds) {
      let q;
      if (statusFilter) {
        q = query(collection(db, 'liveGigs'), where('geohash', '>=', bound[0]), where('geohash', '<=', bound[1]), where('status', '==', statusFilter));
      } else {
        q = query(collection(db, 'liveGigs'), where('geohash', '>=', bound[0]), where('geohash', '<=', bound[1]));
      }
      promises.push(getDocs(q));
    }
    const snapshots = await Promise.all(promises);
    const gigs = [];
    for (const snap of snapshots) {
      snap.forEach((snapDoc) => {
        const gigData = snapDoc.data();
        const gigLocation = { lat: gigData.location.latitude, lng: gigData.location.longitude };
        const distance = calculateDistance(userLocation, gigLocation);
        if (distance <= radiusKm * 1000 && gigData.status !== 'ended' && gigData.status !== 'cancelled') {
          gigs.push({ id: snapDoc.id, ...gigData, distance: Math.round(distance) });
        }
      });
    }
    return gigs.sort((a, b) => {
      if (a.status === 'live' && b.status !== 'live') return -1;
      if (a.status !== 'live' && b.status === 'live') return 1;
      return a.distance - b.distance;
    });
  } catch (error) {
    console.error('❌ Error searching gigs:', error);
    return [];
  }
};

// ============================================
// AUTHENTICATION
// ============================================

export const signInWithGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error) {
    console.error('Google sign-in error:', error);
    throw error;
  }
};

export const signInWithFacebook = async () => {
  try {
    const provider = new FacebookAuthProvider();
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error) {
    console.error('Facebook sign-in error:', error);
    throw error;
  }
};

export const signInWithApple = async () => {
  try {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error) {
    console.error('❌ Apple Sign-In error:', error);
    throw error;
  }
};

export const signOutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Sign-out error:', error);
    throw error;
  }
};

const createOrUpdateUser = async (user) => {
  const userRef = doc(db, 'users', user.uid);
  const userDoc = await getDoc(userRef);
  if (!userDoc.exists()) {
    await setDoc(userRef, {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || 'Anonymous',
      photoURL: user.photoURL || null,
      type: 'audience',
      createdAt: serverTimestamp()
    });
  }
};

// ============================================
// LIVE GIG OPERATIONS
// ============================================

export const createLiveGig = async (gigData, artistId) => {
  try {
    const gigRef = doc(collection(db, 'liveGigs'));
    const geohash = geohashForLocation([gigData.location.lat, gigData.location.lng]);
    const nowMs = Date.now();
    const fiveHoursMs = 5 * 60 * 60 * 1000;
    const endTimeMs = nowMs + fiveHoursMs;
    const gigDocument = {
      ...gigData,
      artistId,
      geohash,
      location: new GeoPoint(gigData.location.lat, gigData.location.lng),
      status: gigData.status || 'live',
      actualStartTimeMs: nowMs,
      scheduledEndTimeMs: endTimeMs,
      actualStartTime: new Date(nowMs),
      startTime: new Date(nowMs),
      scheduledEndTime: new Date(endTimeMs),
      songLimit: gigData.maxQueueSize || gigData.songLimit || 20,
      queuedSongs: (gigData.queuedSongs || []).slice(0, gigData.maxQueueSize || gigData.songLimit || 20),
      votes: {},
      voteTimestamps: {},
      comments: [],
      donations: [],
      playedSongs: []
    };
    await setDoc(gigRef, gigDocument);
    console.log('✅ Gig created with ID:', gigRef.id);
    return gigRef.id;
  } catch (error) {
    console.error('❌ Error creating gig:', error);
    throw error;
  }
};

export const listenToLiveGig = (gigId, callback) => {
  const gigRef = doc(db, 'liveGigs', gigId);
  return onSnapshot(gigRef, (docSnap) => {
    if (docSnap.exists()) {
      const data = docSnap.data();
      const convertTimestamps = (obj) => {
        if (!obj) return obj;
        if (obj?.toDate && typeof obj.toDate === 'function') return obj.toDate();
        if (Array.isArray(obj)) return obj.map(item => convertTimestamps(item));
        if (typeof obj === 'object') {
          const converted = {};
          for (const [key, value] of Object.entries(obj)) { converted[key] = convertTimestamps(value); }
          return converted;
        }
        return obj;
      };
      callback({ id: docSnap.id, ...convertTimestamps(data) });
    }
  }, (error) => { console.error('❌ Error listening to live gig:', error); });
};

export const voteForSong = async (gigId, songId, userId, userLocation, venueLocation) => {
  const distance = calculateDistance(userLocation, venueLocation);
  if (!isWithinRange(userLocation, venueLocation, 1000)) {
    throw new Error(`You must be within 1km of the venue to vote! You are ${Math.round(distance)}m away.`);
  }
  const gigRef = doc(db, 'liveGigs', gigId);
  const cleanSongId = String(Math.floor(songId));
  await updateDoc(gigRef, {
    [`votes.${cleanSongId}`]: increment(1),
    [`voteTimestamps.${cleanSongId}.${userId}`]: serverTimestamp()
  });
};

export const addComment = async (gigId, userId, userName, text, userLocation, venueLocation) => {
  const distance = calculateDistance(userLocation, venueLocation);
  if (!isWithinRange(userLocation, venueLocation, 1000)) {
    throw new Error(`You must be within 1km of the venue to comment! You are ${Math.round(distance)}m away.`);
  }
  const gigRef = doc(db, 'liveGigs', gigId);
  const gigDoc = await getDoc(gigRef);
  const comments = gigDoc.data().comments || [];
  await updateDoc(gigRef, { comments: [...comments, { id: Date.now(), userId, userName, text, timestamp: serverTimestamp() }] });
};

export const processDonation = async (gigId, userId, userName, amount, message, userLocation, venueLocation) => {
  const distance = calculateDistance(userLocation, venueLocation);
  if (!isWithinRange(userLocation, venueLocation, 1000)) {
    throw new Error(`You must be within 1km of the venue to donate! You are ${Math.round(distance)}m away.`);
  }
  const gigRef = doc(db, 'liveGigs', gigId);
  const gigDoc = await getDoc(gigRef);
  const donations = gigDoc.data().donations || [];
  await updateDoc(gigRef, {
    donations: [...donations, { id: Date.now(), userId, userName, amount, message, timestamp: serverTimestamp() }],
    totalEarnings: increment(amount)
  });
};

export const endLiveGig = async (gigId) => {
  const gigRef = doc(db, 'liveGigs', gigId);
  await updateDoc(gigRef, { status: 'ended', endTime: serverTimestamp() });
  console.log('✅ Gig ended');
};

export const updateGigToLive = async (gigId, queuedSongs, masterPlaylist, songLimit = 20) => {
  try {
    const gigRef = doc(db, 'liveGigs', gigId);
    const gigSnap = await getDoc(gigRef);
    if (!gigSnap.exists()) throw new Error('Gig not found');
    const gigData = gigSnap.data();
    const nowMs = Date.now();
    let scheduledEndTimeMs;
    if (gigData.gigDate && gigData.gigTime) {
      const scheduledStartMs = new Date(`${gigData.gigDate}T${gigData.gigTime}`).getTime();
      scheduledEndTimeMs = scheduledStartMs + (5 * 60 * 60 * 1000);
    } else {
      scheduledEndTimeMs = nowMs + (5 * 60 * 60 * 1000);
    }
    await updateDoc(gigRef, {
      status: 'live',
      actualStartTimeMs: nowMs,
      scheduledEndTimeMs,
      actualStartTime: new Date(nowMs),
      startTime: new Date(nowMs),
      scheduledEndTime: new Date(scheduledEndTimeMs),
      queuedSongs: queuedSongs.slice(0, songLimit),
      masterPlaylist,
      songLimit,
      votes: {},
      voteTimestamps: {},
      comments: [],
      donations: [],
      playedSongs: []
    });
    return gigId;
  } catch (error) {
    console.error('❌ Error updating gig:', error);
    throw error;
  }
};

export const extendGigTime = async (gigId, additionalMinutes = 60) => {
  try {
    const gigRef = doc(db, 'liveGigs', gigId);
    const gigSnap = await getDoc(gigRef);
    if (!gigSnap.exists()) throw new Error('Gig not found');
    const gigData = gigSnap.data();
    let newEndTimeMs;
    if (gigData.scheduledEndTimeMs) {
      newEndTimeMs = gigData.scheduledEndTimeMs + (additionalMinutes * 60 * 1000);
    } else if (gigData.scheduledEndTime) {
      const oldEndTime = gigData.scheduledEndTime?.toDate?.() || new Date(gigData.scheduledEndTime);
      newEndTimeMs = oldEndTime.getTime() + (additionalMinutes * 60 * 1000);
    } else {
      newEndTimeMs = Date.now() + (additionalMinutes * 60 * 1000);
    }
    await updateDoc(gigRef, { scheduledEndTimeMs: newEndTimeMs, scheduledEndTime: new Date(newEndTimeMs) });
    return newEndTimeMs;
  } catch (error) {
    console.error('❌ Error extending gig time:', error);
    throw error;
  }
};

export const checkAndCancelExpiredGigs = async (userId) => {
  try {
    const now = new Date();
    const gigsRef = collection(db, 'gigs');
    const q = query(gigsRef, where('artistId', '==', userId), where('status', 'in', ['upcoming', 'confirmed']));
    const snapshot = await getDocs(q);
    const cancelledGigs = [];
    for (const docSnap of snapshot.docs) {
      const gig = { id: docSnap.id, ...docSnap.data() };
      if (gig.date && gig.time) {
        const gigDateTime = new Date(`${gig.date}T${gig.time}`);
        const expiryTime = new Date(gigDateTime.getTime() + (5 * 60 * 60 * 1000));
        if (now > expiryTime) {
          await updateDoc(doc(db, 'gigs', gig.id), { status: 'cancelled', cancelReason: 'Auto-cancelled', cancelledAt: now });
          cancelledGigs.push(gig.venueName);
        }
      }
    }
    return cancelledGigs;
  } catch (error) {
    console.error('❌ Error checking expired gigs:', error);
    return [];
  }
};

export const checkAndSwapSongs = async (gigId, gigData) => {
  try {
    const queuedSongs = gigData.queuedSongs || [];
    const masterPlaylist = gigData.masterPlaylist || [];
    const votes = gigData.votes || {};
    const playedSongs = gigData.playedSongs || [];
    const unplayedGigSongs = queuedSongs.filter(song => !playedSongs.includes(song.id));
    if (unplayedGigSongs.length === 0) return;
    const gigSongIds = queuedSongs.map(s => s.id);
    const availableMasterSongs = masterPlaylist.filter(song => !gigSongIds.includes(song.id));
    if (availableMasterSongs.length === 0) return;
    const lowestGigSong = unplayedGigSongs.reduce((lowest, song) => {
      return (votes[Math.floor(song.id)] || 0) < (votes[Math.floor(lowest.id)] || 0) ? song : lowest;
    });
    const highestMasterSong = availableMasterSongs.reduce((highest, song) => {
      return (votes[Math.floor(song.id)] || 0) > (votes[Math.floor(highest?.id)] || 0) ? song : highest;
    }, availableMasterSongs[0]);
    if ((votes[Math.floor(highestMasterSong.id)] || 0) > (votes[Math.floor(lowestGigSong.id)] || 0)) {
      const newQueue = queuedSongs.filter(song => song.id !== lowestGigSong.id).concat([highestMasterSong])
        .sort((a, b) => (votes[Math.floor(b.id)] || 0) - (votes[Math.floor(a.id)] || 0));
      await updateDoc(doc(db, 'liveGigs', gigId), { queuedSongs: newQueue });
      return true;
    }
    return false;
  } catch (error) {
    console.error('❌ Error in checkAndSwapSongs:', error);
    return false;
  }
};

export const deleteGigFromFirebase = async (gigId) => {
  try {
    await deleteDoc(doc(db, 'liveGigs', gigId));
    console.log('✅ Gig deleted from Firebase');
  } catch (error) {
    console.error('❌ Error deleting gig:', error);
    throw error;
  }
};

export const markAsInterested = async (gigId, userId) => {
  try {
    await setDoc(doc(db, 'liveGigs', gigId, 'interestedUsers', userId), { userId, timestamp: serverTimestamp() });
  } catch (error) { console.error('❌ Error marking interested:', error); throw error; }
};

export const unmarkAsInterested = async (gigId, userId) => {
  try {
    await deleteDoc(doc(db, 'liveGigs', gigId, 'interestedUsers', userId));
  } catch (error) { console.error('❌ Error unmarking interested:', error); throw error; }
};

export const getInterestedCount = async (gigId) => {
  try {
    const snapshot = await getDocs(collection(db, 'liveGigs', gigId, 'interestedUsers'));
    return snapshot.size;
  } catch (error) { return 0; }
};

export const signInWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) { console.error('❌ Email sign-in error:', error); throw error; }
};

export const signUpWithEmail = async (email, password) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    await createOrUpdateUser(userCredential.user);
    return userCredential.user;
  } catch (error) { console.error('❌ Email sign-up error:', error); throw error; }
};

export const hasUserVoted = async (gigId, songId, userId) => {
  try {
    const voteSnap = await getDoc(doc(db, 'liveGigs', String(gigId), 'votes', `${userId}_${songId}`));
    return voteSnap.exists();
  } catch (error) { return false; }
};

export const recordUserVote = async (gigId, songId, userId) => {
  try {
    await setDoc(doc(db, 'liveGigs', String(gigId), 'votes', `${userId}_${songId}`), {
      userId, songId, gigId: String(gigId), timestamp: serverTimestamp()
    });
    const gigRef = doc(db, 'liveGigs', String(gigId));
    const gigSnap = await getDoc(gigRef);
    const gigData = gigSnap.data();
    const currentVotes = gigData?.votes || {};
    const lastVoteTime = gigData?.lastVoteTime || {};
    const songKey = String(songId);
    lastVoteTime[songKey] = new Date().toISOString();
    await updateDoc(gigRef, { [`votes.${songKey}`]: (currentVotes[songKey] || 0) + 1, lastVoteTime });
    return true;
  } catch (error) { console.error('Error recording vote:', error); throw error; }
};

export const getActiveLiveGigForArtist = async (artistId) => {
  try {
    const q = query(collection(db, 'liveGigs'), where('artistId', '==', artistId), where('status', '==', 'live'));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const gigDoc = snapshot.docs[0];
      return { id: gigDoc.id, ...gigDoc.data() };
    }
    return null;
  } catch (error) { console.error('❌ Error checking active gigs:', error); return null; }
};

// ===================================
// ARTIST PROFILE FUNCTIONS
// ===================================

export const saveArtistProfile = async (userId, profileData) => {
  try {
    const profileRef = doc(db, 'users', userId, 'profile', 'data');
    const profileToSave = {
      ...profileData,
      updatedAt: serverTimestamp(),
      profileComplete: !!(profileData.artistName && profileData.fullName && profileData.bio && profileData.genre && profileData.location)
    };
    const existingProfile = await getDoc(profileRef);
    if (!existingProfile.exists()) profileToSave.createdAt = serverTimestamp();
    await setDoc(profileRef, profileToSave, { merge: true });
    return profileToSave;
  } catch (error) { console.error('❌ Error saving profile:', error); throw error; }
};

export const getArtistProfile = async (userId) => {
  try {
    const profileSnap = await getDoc(doc(db, 'users', userId, 'profile', 'data'));
    return profileSnap.exists() ? { id: profileSnap.id, ...profileSnap.data() } : null;
  } catch (error) { console.error('❌ Error getting profile:', error); throw error; }
};

export const isProfileComplete = async (userId) => {
  try {
    const profile = await getArtistProfile(userId);
    if (!profile) return false;
    return !!(profile.artistName && profile.fullName && profile.bio && profile.bio.length >= 50 && profile.genre && profile.location);
  } catch (error) { return false; }
};

export const uploadProfilePhoto = async (userId, file) => {
  console.log('📸 Profile photo upload - TODO: Implement Storage');
  return 'https://via.placeholder.com/150';
};

export const sendEmailVerification = async () => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('No user logged in. Please sign in first.');
    if (user.emailVerified) return { success: true, message: 'Email already verified' };
    await firebaseSendEmailVerification(user);
    return { success: true, message: 'Verification email sent! Check your inbox.' };
  } catch (error) {
    if (error.code === 'auth/too-many-requests') throw new Error('Too many requests. Please wait a few minutes and try again.');
    throw new Error(error.message || 'Failed to send verification email');
  }
};

export const checkEmailVerified = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return false;
    await firebaseReload(user);
    return user.emailVerified;
  } catch (error) { return false; }
};

export const submitSongRequest = async (gigId, song, userId, userName, message, amount, isPaid) => {
  try {
    const gigRef = doc(db, 'liveGigs', String(gigId));
    const requestData = {
      id: `request_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      songId: song.id, songTitle: song.title, songArtist: song.artist,
      requesterId: userId, requesterName: userName,
      message: message || '', amount: amount || 0, isPaid: isPaid || false,
      status: 'pending', timestamp: new Date().toISOString(), createdAt: new Date()
    };
    const gigSnap = await getDoc(gigRef);
    if (!gigSnap.exists()) throw new Error('Gig not found');
    const currentRequests = gigSnap.data().songRequests || [];
    await updateDoc(gigRef, { songRequests: [...currentRequests, requestData] });
    return requestData;
  } catch (error) { console.error('❌ Error submitting song request:', error); throw error; }
};

export const acceptSongRequest = async (gigId, requestId) => {
  try {
    const gigRef = doc(db, 'liveGigs', String(gigId));
    const gigSnap = await getDoc(gigRef);
    if (!gigSnap.exists()) throw new Error('Live gig not found');
    const gigData = gigSnap.data();
    const requests = gigData.songRequests || [];
    const currentQueue = gigData.queuedSongs || [];
    const maxQueueSize = gigData.maxQueueSize || 20;
    const masterPlaylist = gigData.masterPlaylist || [];
    const votes = gigData.votes || {};
    const request = requests.find(r => r.id === requestId);
    if (!request) throw new Error('Request not found');
    const requestedSong = masterPlaylist.find(s => s.id === request.songId);
    if (!requestedSong) throw new Error('Requested song not found in master playlist');
    const songAlreadyInQueue = currentQueue.some(s => s.id === requestedSong.id);
    const updatedRequests = requests.map(req => req.id === requestId ? { ...req, status: 'accepted' } : req);
    if (songAlreadyInQueue) {
      await updateDoc(gigRef, { songRequests: updatedRequests });
      return;
    }
    let finalQueue = [...currentQueue];
    const playedSongs = gigData.playedSongs || [];
    const unplayedQueue = finalQueue.filter(s => !playedSongs.includes(s.id));
    if (finalQueue.length >= maxQueueSize && unplayedQueue.length > 0) {
      const lastUnplayedIndex = finalQueue.map(s => s.id).lastIndexOf(unplayedQueue[unplayedQueue.length - 1].id);
      finalQueue.splice(lastUnplayedIndex, 1);
    }
    finalQueue.push(requestedSong);
    const requestedSongIds = updatedRequests.filter(r => r.status === 'accepted').map(r => r.songId);
    finalQueue.sort((a, b) => {
      const aR = requestedSongIds.includes(a.id), bR = requestedSongIds.includes(b.id);
      if (aR && !bR) return -1;
      if (!aR && bR) return 1;
      return (votes[Math.floor(b.id)] || 0) - (votes[Math.floor(a.id)] || 0);
    });
    await updateDoc(gigRef, { songRequests: updatedRequests, queuedSongs: finalQueue });
  } catch (error) { console.error('❌ Error accepting request:', error); throw error; }
};

export const rejectSongRequest = async (gigId, requestId) => {
  try {
    const gigRef = doc(db, 'liveGigs', String(gigId));
    const gigSnap = await getDoc(gigRef);
    if (!gigSnap.exists()) throw new Error('Live gig not found');
    const requests = gigSnap.data().songRequests || [];
    await updateDoc(gigRef, { songRequests: requests.map(req => req.id === requestId ? { ...req, status: 'rejected' } : req) });
  } catch (error) { console.error('❌ Error rejecting request:', error); throw error; }
};

export const updateJukeboxSettings = async (gigId, settings) => {
  try {
    await updateDoc(doc(db, 'liveGigs', String(gigId)), {
      requestsEnabled: settings.requestsEnabled !== undefined ? settings.requestsEnabled : true,
      jukeboxMode: settings.jukeboxMode || false,
      jukeboxPrice: settings.jukeboxPrice || 0
    });
  } catch (error) { console.error('❌ Error updating settings:', error); throw error; }
};

export const getSortedSongRequests = (requests) => {
  if (!requests || requests.length === 0) return [];
  return [...requests].sort((a, b) => {
    if (a.status === 'pending' && b.status !== 'pending') return -1;
    if (a.status !== 'pending' && b.status === 'pending') return 1;
    if (a.isPaid && !b.isPaid) return -1;
    if (!a.isPaid && b.isPaid) return 1;
    if (a.isPaid && b.isPaid && a.amount !== b.amount) return b.amount - a.amount;
    return (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0);
  });
};

const autoAddSongsToBank = async (songs) => {
  if (!songs || songs.length === 0) return;
  try {
    const snap = await getDocs(query(collection(db, 'backingTracks')));
    const existing = new Set(snap.docs.map(d => `${d.data().title?.toLowerCase()}__${d.data().artist?.toLowerCase()}`));
    const toAdd = songs.filter(s => s.title && s.artist && !existing.has(`${s.title.toLowerCase()}__${s.artist.toLowerCase()}`));
    await Promise.all(toAdd.map(song => addDoc(collection(db, 'backingTracks'), {
      title: song.title.trim(), artist: song.artist.trim(), genre: song.genre || null,
      youtubeId: null, addedByArtist: true, addedAt: serverTimestamp(),
    })));
    if (toAdd.length > 0) console.log(`✅ Auto-added ${toAdd.length} song(s) to song bank`);
  } catch (e) { console.warn('Could not auto-add to song bank:', e.message); }
};

// ===================================
// VENUE & HIRE REQUEST FUNCTIONS
// ===================================

export const saveVenueProfile = async (userId, profileData) => {
  try {
    // Generate a URL-safe slug from venue name
    const slug = (profileData.venueName || '')
      .toLowerCase().trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 60);
    const venueRef = doc(db, 'venues', userId);
    await setDoc(venueRef, { ...profileData, userId, slug, updatedAt: serverTimestamp() }, { merge: true });
    console.log('✅ Venue profile saved');
  } catch (error) { console.error('❌ Error saving venue profile:', error); throw error; }
};

export const getVenueProfile = async (userId) => {
  try {
    const snap = await getDoc(doc(db, 'venues', userId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (error) { console.error('❌ Error getting venue profile:', error); return null; }
};

// Look up a venue profile by its URL slug
export const getVenueProfileBySlug = async (slug) => {
  try {
    const snap = await getDocs(
      query(collection(db, 'venues'), where('slug', '==', slug))
    );
    if (snap.empty) return null;
    const d = snap.docs[0];
    return { id: d.id, userId: d.id, ...d.data() };
  } catch (error) {
    console.error('❌ Error getting venue by slug:', error);
    return null;
  }
};

// Get past confirmed bookings for a venue (for public profile — count + genres)
export const getVenuePublicStats = async (venueId) => {
  try {
    const snap = await getDocs(
      query(collection(db, 'bookings'), where('venueId', '==', venueId))
    );
    const bookings = snap.docs.map(d => d.data());
    const pastBookings = bookings.filter(b => {
      if (!b.eventDate) return false;
      return new Date() > new Date(`${b.eventDate}T${b.eventTime || '23:59'}`);
    });
    // Unique artists booked
    const uniqueArtists = new Set(pastBookings.map(b => b.artistId)).size;
    return {
      totalBookings: pastBookings.length,
      uniqueArtists,
    };
  } catch (error) {
    console.error('❌ Error getting venue public stats:', error);
    return { totalBookings: 0, uniqueArtists: 0 };
  }
};

// Generate a URL-safe slug from artist name
const generateSlug = (name) => {
  return (name || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 60);
};

export const publishArtistProfile = async (userId, profileData) => {
  try {
    const slug = generateSlug(profileData.artistName);
    // Sync ALL fields to artistProfiles — this is the public collection venues search
    await setDoc(doc(db, 'artistProfiles', userId), {
      userId,
      slug,
      artistName: profileData.artistName || '',
      fullName: profileData.fullName || '',
      genre: profileData.genre || '',
      location: profileData.location || '',
      bio: profileData.bio || '',
      socialMedia: profileData.socialMedia || {},
      profilePhoto: profileData.profilePhoto || '',
      priceMin: profileData.priceMin || '',
      priceMax: profileData.priceMax || '',
      availabilityNote: profileData.availabilityNote || '',
      isAvailable: profileData.isAvailable !== false,
      followerCounts: profileData.followerCounts || null,
      followerCountsUpdatedAt: profileData.followerCountsUpdatedAt || null,
      followerCountsBlocked: profileData.followerCountsBlocked || false,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    console.log('✅ Artist profile published to artistProfiles collection');
  } catch (error) { console.warn('Could not publish artist profile:', error.message); }
};

// Queries the flat artistProfiles collection — fast, single read, no auth issues.
// Artists are added here whenever they save their profile via publishArtistProfile.
export const searchArtistProfiles = async () => {
  try {
    const snap = await getDocs(collection(db, 'artistProfiles'));
    const artists = snap.docs.map(d => {
      const data = d.data();
      return {
        id: d.id,
        userId: d.id,
        artistName: data.artistName || '',
        fullName: data.fullName || '',
        genre: data.genre || '',
        location: data.location || '',
        bio: data.bio || '',
        socialMedia: data.socialMedia || {},
        profilePhoto: data.profilePhoto || '',
        priceMin: data.priceMin || '',
        priceMax: data.priceMax || '',
        availabilityNote: data.availabilityNote || '',
        isAvailable: data.isAvailable !== false,
        followerCounts: data.followerCounts || null,
        followerCountsUpdatedAt: data.followerCountsUpdatedAt || null,
        followerCountsBlocked: data.followerCountsBlocked || false,
        gigwaveFollowerCount: data.gigwaveFollowerCount || 0,
      };
    });
    return artists.sort((a, b) => a.artistName.localeCompare(b.artistName));
  } catch (error) {
    console.error('❌ Error fetching artists:', error);
    return [];
  }
};

// Admin: backfill all existing artists into artistProfiles collection.
// Run once from admin dashboard to fix artists who signed up before publishArtistProfile was added.
export const backfillAllArtistProfiles = async () => {
  try {
    const usersSnap = await getDocs(collection(db, 'users'));
    const venuesSnap = await getDocs(collection(db, 'venues'));
    const venueIds = new Set(venuesSnap.docs.map(d => d.id));

    let synced = 0;
    let skipped = 0;

    // Fetch all profiles in parallel
    const profileFetches = usersSnap.docs
      .filter(u => !venueIds.has(u.id))
      .map(async (userDoc) => {
        try {
          const profileSnap = await getDoc(doc(db, 'users', userDoc.id, 'profile', 'data'));
          if (profileSnap.exists()) {
            const profile = profileSnap.data();
            if (profile.artistName) {
              await setDoc(doc(db, 'artistProfiles', userDoc.id), {
                userId: userDoc.id,
                artistName: profile.artistName || '',
                fullName: profile.fullName || '',
                genre: profile.genre || '',
                location: profile.location || '',
                bio: profile.bio || '',
                socialMedia: profile.socialMedia || {},
                profilePhoto: profile.profilePhoto || '',
                priceMin: profile.priceMin || '',
                priceMax: profile.priceMax || '',
                availabilityNote: profile.availabilityNote || '',
                isAvailable: profile.isAvailable !== false,
                followerCounts: profile.followerCounts || null,
                followerCountsBlocked: profile.followerCountsBlocked || false,
                updatedAt: serverTimestamp(),
              }, { merge: true });
              synced++;
            } else {
              skipped++;
            }
          } else {
            skipped++;
          }
        } catch (e) {
          console.warn('Could not sync user:', userDoc.id, e.message);
          skipped++;
        }
      });

    await Promise.all(profileFetches);
    console.log(`✅ Backfill complete: ${synced} synced, ${skipped} skipped`);
    return { synced, skipped };
  } catch (error) {
    console.error('❌ Backfill error:', error);
    throw error;
  }
};

// Fetch a single artist's full profile from artistProfiles (for venue modal)
export const getArtistProfileForVenue = async (artistId) => {
  try {
    const snap = await getDoc(doc(db, 'artistProfiles', artistId));
    if (snap.exists()) {
      const data = snap.data();
      return {
        id: snap.id,
        userId: snap.id,
        artistName: data.artistName || '',
        fullName: data.fullName || '',
        genre: data.genre || '',
        location: data.location || '',
        bio: data.bio || '',
        socialMedia: data.socialMedia || {},
        profilePhoto: data.profilePhoto || '',
        priceMin: data.priceMin || '',
        priceMax: data.priceMax || '',
        availabilityNote: data.availabilityNote || '',
        isAvailable: data.isAvailable !== false,
        followerCounts: data.followerCounts || null,
        followerCountsUpdatedAt: data.followerCountsUpdatedAt || null,
        followerCountsBlocked: data.followerCountsBlocked || false,
        gigwaveFollowerCount: data.gigwaveFollowerCount || 0,
      };
    }
    return null;
  } catch (error) {
    console.error('❌ Error fetching artist profile for venue:', error);
    return null;
  }
};

export const sendHireRequest = async (requestData) => {
  try {
    const requestRef = doc(collection(db, 'hireRequests'));
    const now = new Date().toISOString();
    await setDoc(requestRef, {
      ...requestData,
      status: 'pending',
      negotiationHistory: [{ by: 'venue', action: 'offer', budget: requestData.budget, message: requestData.description || '', timestamp: now }],
      currentBudget: requestData.budget,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return requestRef.id;
  } catch (error) { console.error('❌ Error sending hire request:', error); throw error; }
};

export const listenHireRequestsForVenue = (venueId, callback) => {
  const q = query(collection(db, 'hireRequests'), where('venueId', '==', venueId));
  return onSnapshot(q, snap => {
    const requests = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    requests.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    callback(requests);
  }, err => console.error('Venue hire requests listener:', err));
};

export const listenHireRequestsForArtist = (artistId, callback) => {
  const q = query(collection(db, 'hireRequests'), where('artistId', '==', artistId));
  return onSnapshot(q, snap => {
    const requests = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    requests.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    callback(requests);
  }, err => console.error('Artist hire requests listener:', err));
};

const createBooking = async (bookingData) => {
  try {
    const bookingRef = doc(collection(db, 'bookings'));
    await setDoc(bookingRef, { ...bookingData, status: 'confirmed', createdAt: serverTimestamp() });
    return bookingRef.id;
  } catch (error) { console.error('❌ Error creating booking:', error); throw error; }
};

export const artistRespondToRequest = async (requestId, action, counterBudget, message) => {
  try {
    const reqRef = doc(db, 'hireRequests', requestId);
    const reqSnap = await getDoc(reqRef);
    if (!reqSnap.exists()) throw new Error('Request not found');
    const reqData = reqSnap.data();
    const now = new Date().toISOString();
    const historyEntry = { by: 'artist', action, budget: action === 'counter' ? counterBudget : reqData.currentBudget, message: message || '', timestamp: now };
    const updates = { negotiationHistory: [...(reqData.negotiationHistory || []), historyEntry], updatedAt: serverTimestamp() };
    if (action === 'accept') {
      updates.status = 'accepted';
      await createBooking({ hireRequestId: requestId, venueId: reqData.venueId, venueName: reqData.venueName, venueLocation: reqData.venueLocation, artistId: reqData.artistId, artistName: reqData.artistName, eventDate: reqData.eventDate, eventTime: reqData.eventTime, duration: reqData.duration, agreedBudget: reqData.currentBudget });
    } else if (action === 'decline') {
      updates.status = 'declined';
    } else if (action === 'counter') {
      updates.status = 'countered';
      updates.currentBudget = counterBudget;
    }
    await updateDoc(reqRef, updates);
  } catch (error) { console.error('❌ Error responding to request:', error); throw error; }
};

export const venueRespondToCounter = async (requestId, action, counterBudget, message) => {
  try {
    const reqRef = doc(db, 'hireRequests', requestId);
    const reqSnap = await getDoc(reqRef);
    if (!reqSnap.exists()) throw new Error('Request not found');
    const reqData = reqSnap.data();
    const now = new Date().toISOString();
    const historyEntry = { by: 'venue', action, budget: action === 'counter' ? counterBudget : reqData.currentBudget, message: message || '', timestamp: now };
    const updates = { negotiationHistory: [...(reqData.negotiationHistory || []), historyEntry], updatedAt: serverTimestamp() };
    if (action === 'accept') {
      updates.status = 'accepted';
      await createBooking({ hireRequestId: requestId, venueId: reqData.venueId, venueName: reqData.venueName, venueLocation: reqData.venueLocation, artistId: reqData.artistId, artistName: reqData.artistName, eventDate: reqData.eventDate, eventTime: reqData.eventTime, duration: reqData.duration, agreedBudget: reqData.currentBudget });
    } else if (action === 'decline') {
      updates.status = 'declined';
    } else if (action === 'counter') {
      updates.status = 'pending';
      updates.currentBudget = counterBudget;
    }
    await updateDoc(reqRef, updates);
  } catch (error) { console.error('❌ Error venue responding:', error); throw error; }
};

export const listenBookingsForVenue = (venueId, callback) => {
  const q = query(collection(db, 'bookings'), where('venueId', '==', venueId));
  return onSnapshot(q, snap => {
    const bookings = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    bookings.sort((a, b) => (a.eventDate || '').localeCompare(b.eventDate || ''));
    callback(bookings);
  }, err => console.error('Bookings listener:', err));
};

export const listenBookingsForArtist = (artistId, callback) => {
  const q = query(collection(db, 'bookings'), where('artistId', '==', artistId));
  return onSnapshot(q, snap => {
    const bookings = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    bookings.sort((a, b) => (a.eventDate || '').localeCompare(b.eventDate || ''));
    callback(bookings);
  }, err => console.error('Artist bookings listener:', err));
};

// ===================================
// MASTER PLAYLIST & GIG PLAYLISTS SYNC
// ===================================

export const saveMasterPlaylist = async (userId, songs) => {
  try {
    const playlistRef = doc(db, 'artistData', userId);
    await setDoc(playlistRef, { masterSongs: songs, updatedAt: serverTimestamp() }, { merge: true });
    console.log('✅ Master playlist saved:', songs.length, 'songs');
    await autoAddSongsToBank(songs);
  } catch (error) { console.error('❌ Error saving master playlist:', error); throw error; }
};

export const getMasterPlaylist = async (userId) => {
  try {
    const snap = await getDoc(doc(db, 'artistData', userId));
    return snap.exists() ? snap.data().masterSongs || [] : [];
  } catch (error) { console.error('❌ Error getting master playlist:', error); return []; }
};

export const saveGigPlaylists = async (userId, playlists) => {
  try {
    const playlistRef = doc(db, 'artistData', userId);
    await setDoc(playlistRef, { gigPlaylists: playlists, updatedAt: serverTimestamp() }, { merge: true });
  } catch (error) { console.error('❌ Error saving gig playlists:', error); throw error; }
};

export const getGigPlaylists = async (userId) => {
  try {
    const snap = await getDoc(doc(db, 'artistData', userId));
    return snap.exists() ? snap.data().gigPlaylists || [] : [];
  } catch (error) { console.error('❌ Error getting gig playlists:', error); return []; }
};

export const listenToPlaylists = (userId, callback) => {
  return onSnapshot(doc(db, 'artistData', userId), (docSnap) => {
    if (docSnap.exists()) {
      const data = docSnap.data();
      callback({ masterSongs: data.masterSongs || [], gigPlaylists: data.gigPlaylists || [] });
    }
  }, (error) => { console.error('❌ Error listening to playlists:', error); });
};

// ===================================
// ARTIST STATS (for venue browsing)
// ===================================

export const getArtistStats = async (artistId) => {
  try {
    const gigsSnap = await getDocs(
      query(collection(db, 'liveGigs'), where('artistId', '==', artistId))
    );

    const allGigs = gigsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    const completedGigs = allGigs.filter(g => g.status === 'ended' || g.status === 'live');
    const upcomingGigs = allGigs.filter(g => g.status === 'upcoming');

    // Collect ratings across all gigs
    let totalRating = 0;
    let ratingCount = 0;
    let totalAudience = new Set();
    let totalVotes = 0;
    let totalRequests = 0;

    allGigs.forEach(g => {
      if (g.ratings && Array.isArray(g.ratings)) {
        g.ratings.forEach(r => {
          if (r.rating) { totalRating += r.rating; ratingCount++; }
        });
      }
      if (g.audienceTracking?.joinedUsers) {
        Object.keys(g.audienceTracking.joinedUsers).forEach(uid => totalAudience.add(uid));
      }
      if (g.votes) totalVotes += Object.values(g.votes).reduce((s, v) => s + v, 0);
      if (g.songRequests) totalRequests += g.songRequests.length;
    });

    return {
      totalGigs: allGigs.length,
      completedGigs: completedGigs.length,
      upcomingGigs: upcomingGigs.length,
      averageRating: ratingCount > 0 ? (totalRating / ratingCount).toFixed(1) : null,
      ratingCount,
      totalAudience: totalAudience.size,
      totalVotes,
      totalRequests,
    };
  } catch (error) {
    console.error('❌ Error getting artist stats:', error);
    return {
      totalGigs: 0, completedGigs: 0, upcomingGigs: 0,
      averageRating: null, ratingCount: 0,
      totalAudience: 0, totalVotes: 0, totalRequests: 0,
    };
  }
};

// ===================================
// FOLLOWER COUNT FUNCTIONS
// ===================================

export const saveFollowerCounts = async (userId, followerCounts) => {
  try {
    const profileRef = doc(db, 'users', userId, 'profile', 'data');
    await setDoc(profileRef, {
      followerCounts,
      followerCountsUpdatedAt: serverTimestamp(),
    }, { merge: true });
    console.log('✅ Follower counts saved');
  } catch (error) { console.error('❌ Error saving follower counts:', error); throw error; }
};

export const blockFollowerCounts = async (artistId) => {
  try {
    const profileRef = doc(db, 'users', artistId, 'profile', 'data');
    await setDoc(profileRef, {
      followerCountsBlocked: true,
      followerCountsBlockedAt: serverTimestamp(),
    }, { merge: true });
    console.log('✅ Follower counts blocked for:', artistId);
  } catch (error) { console.error('❌ Error blocking follower counts:', error); throw error; }
};

export const unblockFollowerCounts = async (artistId) => {
  try {
    const profileRef = doc(db, 'users', artistId, 'profile', 'data');
    await setDoc(profileRef, {
      followerCountsBlocked: false,
      followerCountsBlockedAt: null,
    }, { merge: true });
    console.log('✅ Follower counts unblocked for:', artistId);
  } catch (error) { console.error('❌ Error unblocking follower counts:', error); throw error; }
};

export const getAllArtistsWithFollowerCounts = async () => {
  try {
    const usersSnap = await getDocs(collection(db, 'users'));
    const artists = [];
    for (const userDoc of usersSnap.docs) {
      try {
        const profileSnap = await getDoc(doc(db, 'users', userDoc.id, 'profile', 'data'));
        if (profileSnap.exists()) {
          const profile = profileSnap.data();
          if (profile.artistName && profile.followerCounts) {
            artists.push({
              id: userDoc.id,
              email: userDoc.data().email || '',
              artistName: profile.artistName,
              location: profile.location || '',
              socialMedia: profile.socialMedia || {},
              followerCounts: profile.followerCounts || {},
              followerCountsUpdatedAt: profile.followerCountsUpdatedAt || null,
              followerCountsBlocked: profile.followerCountsBlocked || false,
            });
          }
        }
      } catch (e) { /* skip */ }
    }
    return artists;
  } catch (error) { console.error('❌ Error fetching artists:', error); return []; }
};

// ===================================
// RE-EXPORT FIREBASE UTILITIES
// ===================================

export {
  auth,
  firebaseOnAuthStateChanged as onAuthStateChanged,
  db,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
  arrayUnion,
  arrayRemove,
  increment,
  serverTimestamp,
  GeoPoint
};

// ===================================
// FOLLOW SYSTEM
// ===================================

// Follow an artist. Writes to:
//  - followers/{artistId}/users/{userId}  (who follows the artist)
//  - following/{userId}/artists/{artistId} (who the user follows — for feed use later)
// Also increments gigwaveFollowerCount on artistProfiles/{artistId}
export const followArtist = async (artistId, userId, userName) => {
  try {
    const batch = [
      setDoc(doc(db, 'followers', artistId, 'users', userId), {
        userId,
        userName: userName || 'Anonymous',
        followedAt: serverTimestamp(),
      }),
      setDoc(doc(db, 'following', userId, 'artists', artistId), {
        artistId,
        followedAt: serverTimestamp(),
      }),
    ];
    await Promise.all(batch);

    // Increment follower count on artistProfiles
    const profileRef = doc(db, 'artistProfiles', artistId);
    const snap = await getDoc(profileRef);
    if (snap.exists()) {
      await updateDoc(profileRef, {
        gigwaveFollowerCount: (snap.data().gigwaveFollowerCount || 0) + 1,
      });
    }
    console.log('✅ Followed artist:', artistId);
  } catch (error) {
    console.error('❌ Error following artist:', error);
    throw error;
  }
};

// Unfollow an artist
export const unfollowArtist = async (artistId, userId) => {
  try {
    await Promise.all([
      deleteDoc(doc(db, 'followers', artistId, 'users', userId)),
      deleteDoc(doc(db, 'following', userId, 'artists', artistId)),
    ]);

    // Decrement follower count
    const profileRef = doc(db, 'artistProfiles', artistId);
    const snap = await getDoc(profileRef);
    if (snap.exists()) {
      const current = snap.data().gigwaveFollowerCount || 0;
      await updateDoc(profileRef, {
        gigwaveFollowerCount: Math.max(0, current - 1),
      });
    }
    console.log('✅ Unfollowed artist:', artistId);
  } catch (error) {
    console.error('❌ Error unfollowing artist:', error);
    throw error;
  }
};

// Check if a user follows a specific artist
export const isFollowing = async (artistId, userId) => {
  try {
    const snap = await getDoc(doc(db, 'followers', artistId, 'users', userId));
    return snap.exists();
  } catch (error) {
    return false;
  }
};

// Get all artist IDs that a user follows
export const getFollowedArtists = async (userId) => {
  try {
    const snap = await getDocs(collection(db, 'following', userId, 'artists'));
    return snap.docs.map(d => d.id);
  } catch (error) {
    console.error('❌ Error getting followed artists:', error);
    return [];
  }
};

// Listen to a user's followed artists in real-time
export const listenFollowedArtists = (userId, callback) => {
  return onSnapshot(
    collection(db, 'following', userId, 'artists'),
    snap => callback(snap.docs.map(d => d.id)),
    err => console.error('Follow listener error:', err)
  );
};

// ===================================
// IN-APP NOTIFICATION SYSTEM
// ===================================

// Write a notification to every follower of an artist.
// type: 'new_gig' | 'artist_live'
export const notifyFollowers = async (artistId, notification) => {
  try {
    // Get all followers
    const followersSnap = await getDocs(
      collection(db, 'followers', artistId, 'users')
    );
    if (followersSnap.empty) return;

    // Write notification to each follower's inbox in parallel
    const writes = followersSnap.docs.map(followerDoc => {
      const notifRef = doc(collection(db, 'notifications', followerDoc.id, 'items'));
      return setDoc(notifRef, {
        ...notification,
        artistId,
        read: false,
        createdAt: serverTimestamp(),
      });
    });

    await Promise.all(writes);
    console.log(`✅ Notified ${followersSnap.size} followers`);
  } catch (error) {
    // Non-blocking — never fail the main action because of notifications
    console.warn('Could not send notifications:', error.message);
  }
};

// Listen to a user's unread notification count
export const listenUnreadCount = (userId, callback) => {
  const q = query(
    collection(db, 'notifications', userId, 'items'),
    where('read', '==', false)
  );
  return onSnapshot(q, snap => callback(snap.size),
    err => console.warn('Notification listener:', err));
};

// Listen to all notifications for a user (most recent first)
export const listenNotifications = (userId, callback) => {
  const q = query(
    collection(db, 'notifications', userId, 'items'),
    orderBy('createdAt', 'desc'),
    limit(30)
  );
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }, err => console.warn('Notifications listener:', err));
};

// Mark all notifications as read
export const markAllNotificationsRead = async (userId) => {
  try {
    const q = query(
      collection(db, 'notifications', userId, 'items'),
      where('read', '==', false)
    );
    const snap = await getDocs(q);
    await Promise.all(snap.docs.map(d => updateDoc(d.ref, { read: true })));
  } catch (error) {
    console.warn('Could not mark notifications read:', error.message);
  }
};

// Mark a single notification as read
export const markNotificationRead = async (userId, notifId) => {
  try {
    await updateDoc(doc(db, 'notifications', userId, 'items', notifId), { read: true });
  } catch (error) {
    console.warn('Could not mark notification read:', error.message);
  }
};

// ===================================
// WEB PUSH / FCM TOKEN MANAGEMENT
// ===================================


const VAPID_KEY = 'BBAda3E9XAwtLR13TZNsEXdTcG9GV5hHdnuTdrkl_NenY860VPGYlP1T254_1D5LN76u-s-71BUf-F9QX6BUeZY';

let messagingInstance = null;
const getMessagingInstance = () => {
  if (!messagingInstance) messagingInstance = getMessaging(app);
  return messagingInstance;
};

// Request push permission and save FCM token to Firestore
export const requestPushPermission = async (userId) => {
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.log('Push permission denied');
      return null;
    }

    const messaging = getMessagingInstance();
    const token = await getToken(messaging, { vapidKey: VAPID_KEY });

    if (token) {
      // Save token under user — a user can have multiple devices/browsers
      await setDoc(doc(db, 'fcmTokens', userId, 'tokens', token), {
        token,
        userId,
        createdAt: serverTimestamp(),
        userAgent: navigator.userAgent,
      });
      console.log('✅ FCM token saved');
      return token;
    }
    return null;
  } catch (error) {
    console.warn('Could not get push token:', error.message);
    return null;
  }
};

// Remove FCM token on logout
export const removePushToken = async (userId) => {
  try {
    const messaging = getMessagingInstance();
    const token = await getToken(messaging, { vapidKey: VAPID_KEY });
    if (token) {
      await deleteToken(messaging);
      await deleteDoc(doc(db, 'fcmTokens', userId, 'tokens', token));
      console.log('✅ FCM token removed');
    }
  } catch (error) {
    console.warn('Could not remove push token:', error.message);
  }
};

// ===================================
// POST-GIG ANALYTICS
// ===================================

// Returns per-gig analytics for all ended gigs of an artist.
// All data comes from existing liveGigs documents — no new writes needed.
export const getGigAnalytics = async (artistId) => {
  try {
    const snap = await getDocs(
      query(collection(db, 'liveGigs'), where('artistId', '==', artistId))
    );

    const endedGigs = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(g => g.status === 'ended')
      .sort((a, b) => {
        const dA = a.gigDate || a.date || '';
        const dB = b.gigDate || b.date || '';
        return dB.localeCompare(dA);
      });

    const gigStats = endedGigs.map(gig => {
      // Audience count
      const audienceCount = gig.audienceTracking?.joinedUsers
        ? Object.keys(gig.audienceTracking.joinedUsers).length
        : 0;

      // Votes — find most voted song
      const votes = gig.votes || {};
      const totalVotes = Object.values(votes).reduce((s, v) => s + (Number(v) || 0), 0);
      let mostVotedSong = null;
      if (Object.keys(votes).length > 0) {
        const topId = Object.entries(votes).sort((a, b) => b[1] - a[1])[0][0];
        const songs = gig.queuedSongs || gig.masterPlaylist || [];
        const found = songs.find(s => s.id === topId || String(s.id) === topId);
        if (found) mostVotedSong = { title: found.title, artist: found.artist, votes: votes[topId] };
      }

      // Song requests
      const requests = gig.songRequests || [];
      const totalRequests = requests.length;
      // Most requested song
      const reqCounts = {};
      requests.forEach(r => {
        const key = `${r.title}||${r.artist}`;
        reqCounts[key] = (reqCounts[key] || 0) + 1;
      });
      let mostRequested = null;
      if (Object.keys(reqCounts).length > 0) {
        const [topKey, count] = Object.entries(reqCounts).sort((a, b) => b[1] - a[1])[0];
        const [title, artist] = topKey.split('||');
        mostRequested = { title, artist, count };
      }

      // Ratings
      const ratings = gig.ratings || [];
      const avgRating = ratings.length > 0
        ? (ratings.reduce((s, r) => s + (Number(r.rating) || 0), 0) / ratings.length).toFixed(1)
        : null;

      // Tips / donations
      const donations = gig.donations || [];
      const totalTips = donations.reduce((s, d) => s + (Number(d.amount) || 0), 0);
      const tipCount = donations.length;

      return {
        id: gig.id,
        venueName: gig.venueName || 'Unknown Venue',
        gigDate: gig.gigDate || gig.date || '',
        gigTime: gig.gigTime || gig.time || '',
        audienceCount,
        totalVotes,
        mostVotedSong,
        totalRequests,
        mostRequested,
        avgRating,
        ratingCount: ratings.length,
        totalTips,
        tipCount,
      };
    });

    // Overall summary
    const summary = {
      totalGigsPerformed: gigStats.length,
      totalAudience: gigStats.reduce((s, g) => s + g.audienceCount, 0),
      totalVotes: gigStats.reduce((s, g) => s + g.totalVotes, 0),
      totalRequests: gigStats.reduce((s, g) => s + g.totalRequests, 0),
      totalTips: gigStats.reduce((s, g) => s + g.totalTips, 0),
      avgRating: (() => {
        const rated = gigStats.filter(g => g.avgRating !== null);
        if (rated.length === 0) return null;
        return (rated.reduce((s, g) => s + Number(g.avgRating), 0) / rated.length).toFixed(1);
      })(),
      bestGig: gigStats.length > 0
        ? gigStats.reduce((best, g) => g.audienceCount > (best?.audienceCount || 0) ? g : best, gigStats[0])
        : null,
    };

    return { gigStats, summary };
  } catch (error) {
    console.error('❌ Error getting gig analytics:', error);
    return { gigStats: [], summary: null };
  }
};

// ===================================
// AUDIENCE GIG HISTORY
// ===================================

// Returns all gigs an audience member attended, with their personal activity per gig.
// Queries ended gigs and filters by audienceTracking.joinedUsers map.
export const getAudienceGigHistory = async (userId) => {
  try {
    const snap = await getDocs(
      query(collection(db, 'liveGigs'), where('status', '==', 'ended'))
    );

    const attended = [];

    snap.docs.forEach(d => {
      const gig = { id: d.id, ...d.data() };

      // Check if this user joined the gig
      const joinedUsers = gig.audienceTracking?.joinedUsers || {};
      if (!joinedUsers[userId]) return;

      // Songs this user voted for
      const votes = gig.votes || {};
      const myVotes = [];
      const songs = [...(gig.queuedSongs || []), ...(gig.masterPlaylist || [])];
      Object.entries(votes).forEach(([songId, count]) => {
        // votes map is {songId: count} — check userVotes subcollection isn't available here,
        // so we note total votes and flag songs they liked via requests
      });

      // Song requests this user made
      const requests = (gig.songRequests || []).filter(r => r.requesterId === userId);

      // Rating this user gave
      const ratings = gig.ratings || [];
      const myRating = ratings.find(r => r.userId === userId);

      // Tips this user gave
      const donations = (gig.donations || []).filter(d => d.userId === userId);
      const myTipTotal = donations.reduce((s, d) => s + (Number(d.amount) || 0), 0);

      attended.push({
        id: gig.id,
        artistName: gig.artistName || 'Unknown Artist',
        venueName: gig.venueName || 'Unknown Venue',
        gigDate: gig.gigDate || gig.date || '',
        gigTime: gig.gigTime || gig.time || '',
        myRequests: requests,
        myRating: myRating?.artistRating || myRating?.rating || null,
        myTipTotal,
        tipCount: donations.length,
        totalAudience: Object.keys(joinedUsers).length,
      });
    });

    // Sort newest first
    attended.sort((a, b) => b.gigDate.localeCompare(a.gigDate));

    return attended;
  } catch (error) {
    console.error('❌ Error getting audience gig history:', error);
    return [];
  }
};

// ===================================
// AUDIENCE BADGES
// ===================================

// Computes badges from gig history. Pure function — no Firestore calls.
// Pass the result of getAudienceGigHistory().
export const computeAudienceBadges = (gigHistory) => {
  if (!gigHistory || gigHistory.length === 0) return [];

  const badges = [];

  // ── First Timer 🎸 ────────────────────────────────────────
  // Always awarded if they have any history
  badges.push({
    id: 'first_timer',
    emoji: '🎸',
    label: 'First Timer',
    description: 'Joined your first GigWave gig',
    color: '#00d4ff',
    earnedAt: gigHistory[gigHistory.length - 1]?.gigDate, // oldest gig
  });

  // ── Regular 🎵 ────────────────────────────────────────────
  if (gigHistory.length >= 3) {
    badges.push({
      id: 'regular',
      emoji: '🎵',
      label: 'Regular',
      description: 'Attended 3 or more GigWave gigs',
      color: '#a855f7',
    });
  }

  // ── Veteran 🎖️ ───────────────────────────────────────────
  if (gigHistory.length >= 10) {
    badges.push({
      id: 'veteran',
      emoji: '🎖️',
      label: 'Veteran',
      description: 'Attended 10 or more GigWave gigs',
      color: '#f59e0b',
    });
  }

  // ── Superfan 🏆 ───────────────────────────────────────────
  // 3+ gigs with the same artist
  const artistCounts = {};
  gigHistory.forEach(g => {
    artistCounts[g.artistName] = (artistCounts[g.artistName] || 0) + 1;
  });
  const superfanArtist = Object.entries(artistCounts).find(([, count]) => count >= 3);
  if (superfanArtist) {
    badges.push({
      id: 'superfan',
      emoji: '🏆',
      label: 'Superfan',
      description: `Attended 3+ gigs by ${superfanArtist[0]}`,
      color: '#f59e0b',
    });
  }

  // ── Song Whisperer 🎤 ─────────────────────────────────────
  // Had at least one song request accepted
  const hasAccepted = gigHistory.some(g =>
    g.myRequests.some(r => r.status === 'accepted')
  );
  if (hasAccepted) {
    badges.push({
      id: 'song_whisperer',
      emoji: '🎤',
      label: 'Song Whisperer',
      description: 'Had a song request accepted by an artist',
      color: '#22c55e',
    });
  }

  // ── Big Tipper 💰 ─────────────────────────────────────────
  const hasTipped = gigHistory.some(g => g.myTipTotal > 0);
  if (hasTipped) {
    badges.push({
      id: 'big_tipper',
      emoji: '💰',
      label: 'Big Tipper',
      description: 'Tipped an artist at a live gig',
      color: '#22c55e',
    });
  }

  // ── Night Owl 🌙 ──────────────────────────────────────────
  // Joined a gig that started at 10pm or later
  const isNightOwl = gigHistory.some(g => {
    if (!g.gigTime) return false;
    const hour = parseInt(g.gigTime.split(':')[0], 10);
    return hour >= 22 || hour < 4;
  });
  if (isNightOwl) {
    badges.push({
      id: 'night_owl',
      emoji: '🌙',
      label: 'Night Owl',
      description: 'Joined a gig starting at 10pm or later',
      color: '#8b5cf6',
    });
  }

  // ── Critic ⭐ ─────────────────────────────────────────────
  // Rated 3 or more gigs
  const ratedCount = gigHistory.filter(g => g.myRating).length;
  if (ratedCount >= 3) {
    badges.push({
      id: 'critic',
      emoji: '⭐',
      label: 'Critic',
      description: 'Rated 3 or more artists on GigWave',
      color: '#f59e0b',
    });
  }

  // ── Loyal Fan ❤️ ──────────────────────────────────────────
  // Followed 1+ artists on GigWave (checked via gigHistory having > 1 unique artist)
  const uniqueArtists = new Set(gigHistory.map(g => g.artistName)).size;
  if (uniqueArtists >= 3) {
    badges.push({
      id: 'explorer',
      emoji: '🗺️',
      label: 'Explorer',
      description: 'Attended gigs by 3 or more different artists',
      color: '#00d4ff',
    });
  }

  return badges;
};

// ===================================
// VENUE BOOKING RATINGS
// ===================================

// Save a venue's rating for an artist after a confirmed booking.
// Stored on the booking document itself.
export const rateBookingArtist = async (bookingId, venueId, artistId, rating, review) => {
  try {
    await updateDoc(doc(db, 'bookings', bookingId), {
      venueRating: rating,
      venueReview: review || '',
      venueRatedAt: serverTimestamp(),
      venueRatedBy: venueId,
    });
    // Also write to artistRatings collection so it shows in artist stats
    await setDoc(doc(db, 'artistRatings', `${artistId}_${venueId}_${bookingId}`), {
      artistId,
      venueId,
      bookingId,
      rating,
      review: review || '',
      source: 'venue_booking',
      createdAt: serverTimestamp(),
    });
    console.log('✅ Booking rating saved');
  } catch (error) {
    console.error('❌ Error saving booking rating:', error);
    throw error;
  }
};

// ===================================
// PUBLIC ARTIST PROFILE PAGE
// ===================================

// Look up an artist profile by their URL slug.
// slug is stored on artistProfiles when publishArtistProfile is called.
export const getArtistProfileBySlug = async (slug) => {
  try {
    const snap = await getDocs(
      query(collection(db, 'artistProfiles'), where('slug', '==', slug))
    );
    if (snap.empty) return null;
    const d = snap.docs[0];
    return { id: d.id, userId: d.id, ...d.data() };
  } catch (error) {
    console.error('❌ Error getting profile by slug:', error);
    return null;
  }
};

// Get recent ended gigs for an artist (for public profile)
export const getArtistPublicGigs = async (artistId, limitCount = 5) => {
  try {
    const snap = await getDocs(
      query(
        collection(db, 'liveGigs'),
        where('artistId', '==', artistId),
        where('status', '==', 'ended'),
        orderBy('gigDate', 'desc'),
        limit(limitCount)
      )
    );
    return snap.docs.map(d => {
      const g = d.data();
      const audience = g.audienceTracking?.joinedUsers
        ? Object.keys(g.audienceTracking.joinedUsers).length : 0;
      const votes = g.votes
        ? Object.values(g.votes).reduce((s, v) => s + (Number(v) || 0), 0) : 0;
      const ratings = g.ratings || [];
      const avgRating = ratings.length > 0
        ? (ratings.reduce((s, r) => s + (Number(r.rating || r.artistRating) || 0), 0) / ratings.length).toFixed(1)
        : null;
      return {
        id: d.id,
        venueName: g.venueName || '',
        gigDate: g.gigDate || g.date || '',
        gigTime: g.gigTime || g.time || '',
        audience,
        votes,
        avgRating,
      };
    });
  } catch (error) {
    console.error('❌ Error getting artist public gigs:', error);
    return [];
  }
};

// ===================================
// EARNINGS DASHBOARD
// ===================================

// Aggregates all income sources for an artist:
// - Confirmed bookings (agreedBudget)
// - Tips received from live gigs
export const getEarningsData = async (artistId) => {
  try {
    // ── 1. Bookings ──────────────────────────────────────────
    const bookingsSnap = await getDocs(
      query(collection(db, 'bookings'), where('artistId', '==', artistId))
    );
    const bookings = bookingsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    const confirmedBookings = bookings.filter(b => b.status === 'confirmed' || b.agreedBudget);
    const totalFromBookings = confirmedBookings.reduce((s, b) => s + (Number(b.agreedBudget) || 0), 0);

    // Upcoming vs past
    const now = new Date();
    const upcomingBookings = confirmedBookings.filter(b => {
      if (!b.eventDate) return false;
      return new Date(`${b.eventDate}T${b.eventTime || '23:59'}`) > now;
    });
    const pastBookings = confirmedBookings.filter(b => {
      if (!b.eventDate) return true;
      return new Date(`${b.eventDate}T${b.eventTime || '23:59'}`) <= now;
    });

    // ── 2. Tips from live gigs ───────────────────────────────
    const gigsSnap = await getDocs(
      query(
        collection(db, 'liveGigs'),
        where('artistId', '==', artistId),
        where('status', '==', 'ended')
      )
    );
    const endedGigs = gigsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    let totalTips = 0;
    let tipsByGig = [];
    for (const gig of endedGigs) {
      const tipsSnap = await getDocs(collection(db, 'liveGigs', gig.id, 'tips'));
      const gigTips = tipsSnap.docs.map(d => d.data());
      const gigTipTotal = gigTips.reduce((s, t) => s + (Number(t.amount) || 0), 0);
      totalTips += gigTipTotal;
      if (gigTipTotal > 0) {
        tipsByGig.push({
          gigId: gig.id,
          venueName: gig.venueName || '',
          gigDate: gig.gigDate || '',
          tipTotal: gigTipTotal,
          tipCount: gigTips.length,
        });
      }
    }

    // ── 3. Monthly breakdown (bookings only) ─────────────────
    const monthlyMap = {};
    for (const b of pastBookings) {
      if (!b.eventDate) continue;
      const key = b.eventDate.slice(0, 7); // YYYY-MM
      if (!monthlyMap[key]) monthlyMap[key] = { month: key, bookingIncome: 0, tips: 0, bookingCount: 0 };
      monthlyMap[key].bookingIncome += Number(b.agreedBudget) || 0;
      monthlyMap[key].bookingCount += 1;
    }
    for (const t of tipsByGig) {
      if (!t.gigDate) continue;
      const key = t.gigDate.slice(0, 7);
      if (!monthlyMap[key]) monthlyMap[key] = { month: key, bookingIncome: 0, tips: 0, bookingCount: 0 };
      monthlyMap[key].tips += t.tipTotal;
    }
    const monthly = Object.values(monthlyMap).sort((a, b) => b.month.localeCompare(a.month));

    return {
      summary: {
        totalFromBookings,
        totalTips,
        grandTotal: totalFromBookings + totalTips,
        confirmedBookingCount: confirmedBookings.length,
        upcomingBookingCount: upcomingBookings.length,
        paidGigCount: pastBookings.length,
      },
      upcomingBookings,
      pastBookings,
      tipsByGig,
      monthly,
    };
  } catch (error) {
    console.error('❌ Error getting earnings data:', error);
    return {
      summary: { totalFromBookings: 0, totalTips: 0, grandTotal: 0, confirmedBookingCount: 0, upcomingBookingCount: 0, paidGigCount: 0 },
      upcomingBookings: [], pastBookings: [], tipsByGig: [], monthly: [],
    };
  }
};

// ===================================
// VENUE PREFERRED ARTISTS
// ===================================

// Stored at venues/{venueId}/preferredArtists/{artistId}
export const addPreferredArtist = async (venueId, artist) => {
  try {
    await setDoc(doc(db, 'venues', venueId, 'preferredArtists', artist.userId || artist.id), {
      artistId: artist.userId || artist.id,
      artistName: artist.artistName || '',
      genre: artist.genre || '',
      location: artist.location || '',
      priceMin: artist.priceMin || '',
      priceMax: artist.priceMax || '',
      isAvailable: artist.isAvailable !== false,
      addedAt: serverTimestamp(),
    });
  } catch (error) { console.error('❌ Error adding preferred artist:', error); throw error; }
};

export const removePreferredArtist = async (venueId, artistId) => {
  try {
    await deleteDoc(doc(db, 'venues', venueId, 'preferredArtists', artistId));
  } catch (error) { console.error('❌ Error removing preferred artist:', error); throw error; }
};

export const getPreferredArtists = async (venueId) => {
  try {
    const snap = await getDocs(collection(db, 'venues', venueId, 'preferredArtists'));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) { console.error('❌ Error getting preferred artists:', error); return []; }
};

// ===================================
// ARTIST AVAILABILITY CALENDAR
// ===================================

// Blocked dates stored as a single doc: artists/{artistId}/availability/blocked
// { dates: ['2026-03-20', '2026-03-21', ...] }

export const getBlockedDates = async (artistId) => {
  try {
    const snap = await getDoc(doc(db, 'artists', artistId, 'availability', 'blocked'));
    return snap.exists() ? (snap.data().dates || []) : [];
  } catch (error) { console.error('❌ Error getting blocked dates:', error); return []; }
};

export const saveBlockedDates = async (artistId, dates) => {
  try {
    await setDoc(doc(db, 'artists', artistId, 'availability', 'blocked'), {
      dates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) { console.error('❌ Error saving blocked dates:', error); throw error; }
};

// ===================================
// FOLLOWED ARTIST GIGS (DISCOVERY FEED)
// ===================================

// Get upcoming/live gigs from artists a user follows.
export const getFollowedArtistGigs = async (userId) => {
  try {
    const followSnap = await getDocs(collection(db, 'following', userId, 'artists'));
    if (followSnap.empty) return [];
    const artistIds = followSnap.docs.map(d => d.id);

    // Chunk into groups of 10 (Firestore 'in' limit)
    const chunks = [];
    for (let i = 0; i < artistIds.length; i += 10) {
      chunks.push(artistIds.slice(i, i + 10));
    }

    const allGigs = [];
    await Promise.all(chunks.map(async (chunk) => {
      const snap = await getDocs(
        query(
          collection(db, 'liveGigs'),
          where('artistId', 'in', chunk),
          where('status', 'in', ['upcoming', 'live'])
        )
      );
      snap.docs.forEach(d => allGigs.push({ id: d.id, ...d.data() }));
    }));

    // Sort: live first, then upcoming by date
    allGigs.sort((a, b) => {
      if (a.status === 'live' && b.status !== 'live') return -1;
      if (b.status === 'live' && a.status !== 'live') return 1;
      return (a.gigDate || '').localeCompare(b.gigDate || '');
    });

    return allGigs;
  } catch (error) {
    console.error('❌ Error getting followed artist gigs:', error);
    return [];
  }
};

// ===================================
// ARTIST PROFILE VISIBILITY
// ===================================

// Toggle public profile on/off.
// Sets profileDisabled flag on artistProfiles/{userId}.
export const setProfileDisabled = async (userId, disabled) => {
  try {
    await setDoc(doc(db, 'artistProfiles', userId), {
      profileDisabled: disabled,
      profileDisabledAt: disabled ? serverTimestamp() : null,
    }, { merge: true });
    console.log(`✅ Profile ${disabled ? 'disabled' : 'enabled'}`);
  } catch (error) {
    console.error('❌ Error updating profile visibility:', error);
    throw error;
  }
};